<template>
  <div>
    <NuxtLayout name="default">
      <div>
        <HeroStandardApollo
          :title="study.title!"
          :full-width="false"
          :bg-image-source="study.heroImage!"
          :display-text="true"
          :gradient-opacity="0.75"
          title-size="large"
        ></HeroStandardApollo>

        <section class="pt-space-xl bg-white pb-space-xxl">
          <div class="container">
            <div class="row">
              <div class="col-12 col-lg-9">
                <h2 class="h2-medium pb-space-sm">
                  {{ study.introTitle }}
                </h2>
                <div class="pb-space-lg" v-html="study.introDescription"></div>
              </div>
            </div>
            <div class="row card-deck row-cols-1 row-cols-md-2 row-cols-lg-3">
              <div v-for="interests in interestAreas" :key="interests.id" class="col mb-space-sm">
                <CardImageTile
                  :bg-image-src="interests.image!"
                  :clickable-card="true"
                  :title="interests.title!"
                  :text="interests.description!"
                  :cta-text="PAGE_DEFAULTS.ctaText"
                  :cta-link="interests.detailPage!"
                  class="space-mb-sm"
                  :display-cta-button="true"
                  @card-click="handleCardClick(interests, $event, 'hover card')"
                  @cta-click="handleLinkEvent(interests, $event, 'cta button')"
                  @card-enter="handleEnterKeyEvent(interests.detailPage!)"
                />
              </div>
            </div>
          </div>
        </section>
        <SectionRfiMultiStep :bg-image="bgImg" />
      </div>
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import bgImg from "@/assets/img/rfi-bg-overlay.png";
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardImageTile } from "@rds-vue-ui/card-image-tile";
import { HeroStandardApollo } from "@rds-vue-ui/hero-standard-apollo";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";
import type { InterestAreaType, Study } from "~/server/resolvers/resolvers-types";
import type { StudyQuery } from "~/types/graphql-operations";
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"]
  }
});

type InterestAreaItem = NonNullable<NonNullable<StudyQuery["allInterestAreaType"]>[number]>;

enum PAGE_DEFAULTS {
  ctaText = "Learn more"
}

/*
 ********************************************
 * $page
 ********************************************
 */

const $page = await usePageQuery<StudyQuery>(
  /* GraphQL */ `
    #graphql
    query Study($studyId: ID!, $sort: sortInput) {
      study(id: $studyId) {
        detailPage
        metaTitle
        metaDescription
        heroImage
        title
        introTitle
        introDescription
      }
      allInterestAreaType(sort: $sort) {
        id
        title
        description
        detailPage
        image
      }
    }
  `,
  {
    studyId: "f88d1424-6657-4b63-970f-7e82f69d8c03",
    sort: {
      by: "title",
      order: "ASC"
    }
  },
  {
    key: "all-study-pages"
  }
);

// `study` is nullable in the schema; this page renders it unconditionally
// throughout, so a missing study record is a controlled error rather than a
// silent crash.
if (!$page?.study) {
  throw createError({ message: `${String(useRoute().name)} query failed` });
}

const study = $page.study;
const interestAreas: InterestAreaItem[] = ($page.allInterestAreaType ?? []).filter(
  (item): item is InterestAreaItem => item !== null
);

// generateStudyPageSchema's params are typed against the full resolver
// schema, but only read the fields this query selects (metaTitle,
// detailPage, metaDescription, heroImage; title/description/image/detailPage
// on each area) -- see useSchemaService.ts. The structural mismatch on
// unselected required fields (id, contentHub, icon, etc.) that nothing here
// reads is safe to bridge.
const jsonLdMarkup = useSchemaService().generateStudyPageSchema(
  study as unknown as Study,
  interestAreas as unknown as InterestAreaType[]
);
useHead({
  title: study.metaTitle,
  meta: [
    {
      name: "description",
      content: study.metaDescription
    }
  ],
  link: [
    {
      rel: "canonical",
      href: study.detailPage as string
    }
  ],
  script: [jsonLdMarkup]
});

/*
 ********************************************
 * Methods
 ********************************************
 */
const handleCardClick = (interest: InterestAreaItem, e: Event, component: string) => {
  e.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    study.introTitle ? study.introTitle.toLowerCase() : "",
    interest.title ? interest.title.toLowerCase() : "",
    component.toLowerCase()
  );
  navigateTo(interest.detailPage!, {
    open: {
      target: "_self"
    }
  });
};

const handleLinkEvent = (interest: InterestAreaItem, e: Event, component: string) => {
  const formattedText = `${interest.title} ${PAGE_DEFAULTS.ctaText}`.toLowerCase();
  e.preventDefault();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    formattedText,
    interest.title ? interest.title.toLowerCase() : "",
    component.toLowerCase()
  );
  navigateTo(interest.detailPage!, {
    open: {
      target: "_self"
    }
  });
};

const handleEnterKeyEvent = (url: string) => {
  navigateTo(url, {
    open: {
      target: "_self"
    }
  });
};
</script>
<style></style>
